import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle2, ScanLine } from 'lucide-react'
import { productLabelsApi, type LabelScanCheck, type ProductLabel } from '@/api/productLabelsApi'
import { useVerifyLabel } from '@/hooks/useProductLabels'
import { useAuth } from '@/providers/AuthProvider'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { LABEL_SIGNATURE_MEANINGS } from '@/lib/productLabels'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/**
 * Post-print verification of one label (z8nrz7dt48): scan (or type) the DIN
 * and product code off the printed label, the server checks them against the
 * product, and only a match can be signed — which marks the label applied.
 *
 * A wedge scanner is a keyboard: it types the code and presses Enter, which
 * moves to the next code and, on the last, runs the check. THE VERDICT IS THE
 * SERVER'S, as in EquipmentScanField: the check is a call, re-run on sign, so a
 * mismatch is shown with expected and scanned and never spends a signature.
 */
export function VerifyLabelDialog({
  label,
  onClose,
  onVerified,
}: {
  label: ProductLabel | null
  onClose: () => void
  /** After a successful verification, before `onClose`. */
  onVerified?: (label: ProductLabel) => void
}) {
  const { user } = useAuth()
  const verify = useVerifyLabel()
  const [din, setDin] = useState('')
  const [code, setCode] = useState('')
  const [check, setCheck] = useState<LabelScanCheck | null>(null)
  const [checking, setChecking] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const codeRef = useRef<HTMLInputElement>(null)
  // The codes the shown verdict is for: Enter and leaving the field both check,
  // and the same pair is never sent twice.
  const checkedRef = useRef<string | null>(null)
  const needsCode = !!label?.productCode

  useEffect(() => {
    setDin('')
    setCode('')
    setCheck(null)
    setError(null)
    checkedRef.current = null
  }, [label?.id])

  async function runCheck(nextDin = din, nextCode = code) {
    if (!label || !nextDin.trim()) return
    const key = `${nextDin}\u0000${nextCode}`
    if (checkedRef.current === key) return
    checkedRef.current = key
    setChecking(true)
    setError(null)
    try {
      const result = await productLabelsApi.check(label.id, { din: nextDin, productCode: nextCode || undefined })
      setCheck(result)
      // A match leaves only the signature: put the cursor in it.
      if (result.ok) setTimeout(() => document.getElementById('signature-password')?.focus(), 0)
    } catch (e) {
      checkedRef.current = null
      setError(apiErrorMessage(e, 'Could not check the label.'))
    } finally {
      setChecking(false)
    }
  }

  function confirm(signingToken: string) {
    if (!label) return
    setError(null)
    verify.mutate(
      {
        id: label.id,
        din,
        productCode: code || undefined,
        signingToken,
        signatureMeaning: LABEL_SIGNATURE_MEANINGS.postPrint,
      },
      {
        onSuccess: verified => {
          onVerified?.(verified)
          onClose()
        },
        onError: e => setError(apiErrorMessage(e, 'Could not verify the label.')),
      },
    )
  }

  return (
    <SignatureDialog
      open={!!label}
      onOpenChange={o => !o && onClose()}
      title={label ? `Verify label #${label.number} after printing` : 'Verify label'}
      description='Scan the barcodes on the printed label. A match is signed and the label counts as applied.'
      confirmLabel='Verify & Sign'
      signerName={user?.fullName || user?.email}
      confirmDisabled={!check?.ok || checking}
      busy={verify.isPending}
      error={error}
      onConfirm={confirm}
    >
      {label && (
        <div className='space-y-3'>
          <div className='rounded-md border bg-muted/40 px-3 py-2 text-sm'>
            <div className='text-xs text-muted-foreground'>Expected on the label</div>
            <div className='font-mono'>
              {label.din}
              {label.productCode ? ` · ${label.productCode}` : ''}
            </div>
          </div>
          <div className='space-y-1.5'>
            <Label htmlFor='verify-label-din'>DIN</Label>
            <div className='relative'>
              <ScanLine className='pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
              <Input
                id='verify-label-din'
                className='pl-7 font-mono'
                autoComplete='off'
                autoFocus
                placeholder='Scan or type the DIN'
                value={din}
                onChange={e => {
                  setDin(e.target.value)
                  setCheck(null)
                }}
                onKeyDown={e => {
                  if (e.key !== 'Enter') return
                  e.preventDefault()
                  if (needsCode && !code.trim()) codeRef.current?.focus()
                  else void runCheck()
                }}
              />
            </div>
          </div>
          {needsCode && (
            <div className='space-y-1.5'>
              <Label htmlFor='verify-label-code'>Product code</Label>
              <div className='relative'>
                <ScanLine className='pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground' />
                <Input
                  id='verify-label-code'
                  ref={codeRef}
                  className='pl-7 font-mono'
                  autoComplete='off'
                  placeholder='Scan or type the product code'
                  value={code}
                  onChange={e => {
                    setCode(e.target.value)
                    setCheck(null)
                  }}
                  onKeyDown={e => {
                    if (e.key !== 'Enter') return
                    e.preventDefault()
                    void runCheck()
                  }}
                  onBlur={() => {
                    if (din.trim() && code.trim()) void runCheck()
                  }}
                />
              </div>
            </div>
          )}
          {checking && <p className='text-xs text-muted-foreground'>Checking…</p>}
          {check?.ok && (
            <p className='flex items-center gap-1.5 text-sm text-emerald-700'>
              <CheckCircle2 className='h-4 w-4' />
              Matches label #{label.number}.
            </p>
          )}
          {check && !check.ok && (
            <div role='alert' className='space-y-0.5 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive'>
              {[...check.mismatches, ...(check.belongsTo ? [check.belongsTo] : [])].map(line => (
                <p key={line} className='flex items-start gap-1.5'>
                  <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                  {line}
                </p>
              ))}
            </div>
          )}
        </div>
      )}
    </SignatureDialog>
  )
}
